import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ethers } from 'ethers';
import PaymentVaultABI from 'abis/PaymentVault';
import PayoutExecutorABI from 'abis/PayoutExecutor';
import RevenueSplitterABI from 'abis/RevenueSplitter';
import { getRevenueItems } from 'helpers/paymentRequest.api';
import {
  createDistributionBatch,
  executeDistributionBatch,
  getDistributionBatchById,
} from 'helpers/distributionBatch.api';
import { buttonStyles } from '../css/commonStyles';
import {
  cancelBtnSx,
  colors,
  confirmBtnSx,
  dateFormFieldStyles,
  fieldBlockStyles,
  fieldLabelStyles,
  formFieldStyles,
  formGridSx,
  requiredSpanSx,
  makeSelectSx,
  modalPaperStyles,
  dialogTitleSx,
  primaryValueStyles,
  selectMenuProps,
  modalInnerTableCardSx,
  modalInnerTableContainerSx,
  previewSummaryCardSx,
  previewSummaryGridSx,
  previewSummaryLabelSx,
  previewSummaryValueSx,
  tableContainerStyles,
} from '../css/revenueItems.styles';
import { formatDateTime, formatRevenueType } from '../utils/revenueItems.utils';
import {
  formatRecipientTypeLabel,
  formatRevenueSharingErrorMessage,
  isTxUserRejection,
  isValidEthAddress,
  parseLpHolderCsvText,
  sortPayoutDistributions,
} from '../utils/revenueSharing.utils';
import { ensureRevenueSharingMessageLayer, revenueSharingMessage } from '../utils/revenueSharingMessage';

const formatUsdt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '0');
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
};

const shortenAddress = (addr, head = 8, tail = 6) => {
  if (!addr || typeof addr !== 'string') return '—';
  const s = addr.startsWith('0x') ? addr : `0x${addr}`;
  if (s.length <= head + tail + 2) return s;
  return `${s.slice(0, head + 2)}…${s.slice(-tail)}`;
};

const parseVaultAddress = (addr) => {
  const v = (addr || '').trim();
  return /^0x[0-9a-fA-F]{40}$/.test(v) ? v : '';
};

const STEPS = ['Cycle period', 'Revenue items', 'Split version', 'LP holders', 'Preview'];

const previewFromBatchRecord = (batch) => {
  const distributions = batch?.distributions || [];
  const sumByType = (type) =>
    String(
      distributions
        .filter((d) => d.recipientType === type)
        .reduce((s, d) => s + Number(d.amount), 0),
    );
  return {
    gross: batch?.totalRevenue || '0',
    lpTotal: sumByType('lp_holder'),
    treasuryTotal: sumByType('treasury'),
    platformTotal: sumByType('platform'),
    distributions,
  };
};

async function ensureCorrectChain(targetChainId) {
  if (!window.ethereum || targetChainId == null || targetChainId === '') return;
  const tid = Number(targetChainId);
  if (Number.isNaN(tid)) return;
  const hexChainId = `0x${tid.toString(16)}`;
  const current = await window.ethereum.request({ method: 'eth_chainId' });
  if (String(current).toLowerCase() === hexChainId.toLowerCase()) return;
  await window.ethereum.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: hexChainId }],
  });
}

const CreateBatchModal = ({
  open,
  onClose,
  currentDao,
  vaultAddress,
  existingBatchId = null,
  executeOnly = false,
  viewOnly = false,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [splitVersion, setSplitVersion] = useState('');
  const [splitRows, setSplitRows] = useState([]);
  const [lpHolders, setLpHolders] = useState([]);
  const [lpFileName, setLpFileName] = useState('');
  const [newLpAddress, setNewLpAddress] = useState('');
  const lpFileInputRef = useRef(null);
  const autoSelectedStep1Ref = useRef(false);
  const [eligibleItems, setEligibleItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revenueItemsLoading, setRevenueItemsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState(null);
  const [isDaoOperator, setIsDaoOperator] = useState(false);
  const [walletRoleChecked, setWalletRoleChecked] = useState(false);
  const [chainCtx, setChainCtx] = useState(null);
  const [batchVaultAddress, setBatchVaultAddress] = useState('');

  /** Current DAO vault — used when creating a new batch. */
  const daoVault = useMemo(() => parseVaultAddress(vaultAddress || currentDao?.vaultAddress), [
    vaultAddress,
    currentDao?.vaultAddress,
  ]);

  const effectiveBatchId = batchId || existingBatchId || null;

  /** Vault for on-chain ops: saved batch vault when distributing/viewing; DAO vault for new cycles. */
  const effectiveVault = useMemo(() => {
    if (effectiveBatchId) {
      return batchVaultAddress;
    }
    return daoVault;
  }, [effectiveBatchId, batchVaultAddress, daoVault]);

  const isReadOnly = viewOnly || batchStatus === 'executed';

  const resetState = () => {
    setActiveStep(executeOnly ? 4 : 0);
    setBatchId(null);
    setBatchStatus(null);
    setPeriodStart('');
    setPeriodEnd('');
    setSplitVersion('');
    setSplitRows([]);
    setLpHolders([]);
    setLpFileName('');
    setNewLpAddress('');
    autoSelectedStep1Ref.current = false;
    setEligibleItems([]);
    setSelectedIds([]);
    setPreview(null);
    setRevenueItemsLoading(false);
    setPreviewLoading(false);
    setSubmitAction(null);
    setIsDaoOperator(false);
    setWalletRoleChecked(false);
    setChainCtx(null);
    setBatchVaultAddress('');
  };

  const loadSplitConfigs = useCallback(async () => {
    if (!effectiveVault || !window.ethereum) {
      setIsDaoOperator(false);
      setWalletRoleChecked(true);
      return;
    }
    try {
    await ensureCorrectChain(currentDao?.chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const vault = new ethers.Contract(effectiveVault, PaymentVaultABI, provider);
    const payoutExecAddr = await vault.payoutExecutor();
    const payoutExec = new ethers.Contract(payoutExecAddr, PayoutExecutorABI, provider);
    const splitterAddr = await payoutExec.splitter();
    const splitter = new ethers.Contract(splitterAddr, RevenueSplitterABI, provider);
    const daoOperatorRole = await vault.DAO_OPERATOR_ROLE();
    const [av, all] = await Promise.all([splitter.activeVersion(), splitter.getAllSplitConfigs()]);
    const versions = all[0].map((x) => x.toNumber());
    const mapped = versions.map((ver, i) => ({
      version: ver,
      lp: all[1][i].toNumber(),
      dao: all[2][i].toNumber(),
      platform: all[3][i].toNumber(),
    }));
    setSplitRows(mapped);
    if (!splitVersion) setSplitVersion(String(av.toNumber()));

    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    const account = accounts?.[0];
    if (account) {
      const ok = await vault.hasRole(daoOperatorRole, account);
      setIsDaoOperator(ok);
    } else {
      setIsDaoOperator(false);
    }

    const [daoTreasury, platformTreasury, acceptedToken] = await Promise.all([
      vault.daoTreasury(),
      vault.platformTreasury(),
      vault.acceptedToken(),
    ]);
    const token = new ethers.Contract(acceptedToken, ['function decimals() view returns (uint8)'], provider);
    const decimals = await token.decimals();

    setChainCtx({
      provider,
      vault,
      payoutExec,
      daoTreasury,
      platformTreasury,
      decimals: Number(decimals),
    });
    } finally {
      setWalletRoleChecked(true);
    }
  }, [effectiveVault, currentDao?.chainId, splitVersion]);

  const loadEligibleItems = useCallback(async () => {
    if (!currentDao?._id) return;
    setRevenueItemsLoading(true);
    try {
      const filters = {
        daoId: currentDao._id,
        limit: 100,
        status: 'confirmed',
        sortOrder: 'desc',
      };
      if (effectiveBatchId) {
        filters.batchIdForEdit = effectiveBatchId;
      } else {
        filters.eligibleForBatch = 'true';
        if (daoVault) {
          filters.vaultAddress = daoVault;
        }
      }
      const res = await getRevenueItems(filters);
      setEligibleItems(res?.data || []);
    } finally {
      setRevenueItemsLoading(false);
    }
  }, [currentDao?._id, effectiveBatchId, daoVault]);

  const loadBatch = useCallback(async () => {
    if (!existingBatchId) return;
    setLoading(true);
    try {
      const res = await getDistributionBatchById(existingBatchId);
      const b = res?.data;
      if (!b) return;
      setBatchId(b._id);
      setBatchStatus(b.status);
      const resolvedBatchVault =
        parseVaultAddress(b.vaultAddress) ||
        parseVaultAddress(b.revenueItems?.[0]?.vaultAddress) ||
        '';
      setBatchVaultAddress(resolvedBatchVault);
      setPeriodStart(b.periodStart ? new Date(b.periodStart).toISOString().slice(0, 10) : '');
      setPeriodEnd(b.periodEnd ? new Date(b.periodEnd).toISOString().slice(0, 10) : '');
      setSplitVersion(String(b.splitVersion));
      setLpHolders(b.lpHolders || []);
      setSelectedIds((b.revenueItemIds || []).map(String));
      if (b.revenueItems?.length) {
        setEligibleItems(b.revenueItems);
      }
      if (viewOnly || b.status === 'executed') {
        setPreview(previewFromBatchRecord(b));
      }
      if (executeOnly || viewOnly || existingBatchId) {
        setActiveStep(4);
      }
    } catch (err) {
      revenueSharingMessage.error(err?.message || 'Failed to load batch');
    } finally {
      setLoading(false);
    }
  }, [existingBatchId, executeOnly, viewOnly]);

  useEffect(() => {
    if (!open) return;
    ensureRevenueSharingMessageLayer();
    resetState();
    if (existingBatchId) {
      setBatchId(existingBatchId);
      loadBatch();
    }
    if (executeOnly || viewOnly) {
      setActiveStep(4);
    }
  }, [open, existingBatchId, executeOnly, viewOnly]);

  useEffect(() => {
    if (!open || !effectiveVault || viewOnly) return;
    loadSplitConfigs().catch((e) => revenueSharingMessage.error(e?.message || 'Failed to load vault'));
  }, [open, effectiveVault, loadSplitConfigs, viewOnly]);

  useEffect(() => {
    if (!open || activeStep !== 1) return;
    loadEligibleItems().catch((e) => revenueSharingMessage.error(e?.message || 'Failed to load revenue items'));
  }, [open, activeStep, loadEligibleItems]);

  const itemsInCycle = useMemo(() => {
    const vaultNorm = effectiveVault?.toLowerCase();
    const matchesVault = (item) => {
      if (!vaultNorm) return true;
      const itemVault = item.vaultAddress?.toLowerCase();
      return itemVault === vaultNorm;
    };

    const inPeriod = (item) => {
      if (!periodStart || !periodEnd) return true;
      const start = new Date(periodStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(periodEnd);
      end.setHours(23, 59, 59, 999);
      const t = new Date(item.createdAt).getTime();
      return t >= start.getTime() && t <= end.getTime();
    };

    return eligibleItems.filter((item) => matchesVault(item) && inPeriod(item));
  }, [eligibleItems, periodStart, periodEnd, effectiveVault]);

  useEffect(() => {
    autoSelectedStep1Ref.current = false;
  }, [periodStart, periodEnd]);

  useEffect(() => {
    if (!open || activeStep !== 1 || !itemsInCycle.length) return;
    if (autoSelectedStep1Ref.current || existingBatchId) return;
    setSelectedIds(itemsInCycle.map((i) => String(i._id)));
    autoSelectedStep1Ref.current = true;
  }, [open, activeStep, itemsInCycle, existingBatchId]);

  const allInCycleSelected =
    itemsInCycle.length > 0 && selectedIds.length === itemsInCycle.length;
  const someInCycleSelected =
    selectedIds.length > 0 && selectedIds.length < itemsInCycle.length;

  const selectedItems = useMemo(
    () => itemsInCycle.filter((i) => selectedIds.includes(String(i._id))),
    [itemsInCycle, selectedIds],
  );

  const sortedPayoutDistributions = useMemo(
    () => (preview?.distributions ? sortPayoutDistributions(preview.distributions) : []),
    [preview],
  );

  const buildBatchBody = () => ({
    daoId: currentDao._id,
    vaultAddress: daoVault,
    periodStart: periodStart || null,
    periodEnd: periodEnd || null,
    revenueItemIds: selectedIds,
    splitVersion: Number(splitVersion),
    lpHolders,
    distributions: preview?.distributions || [],
  });

  /** Creates a locked batch when none exists yet (new cycle). */
  const createBatchIfNeeded = async () => {
    if (effectiveBatchId) {
      return effectiveBatchId;
    }
    if (existingBatchId && loading) {
      throw new Error('Batch is still loading. Please wait and try again.');
    }
    const res = await createDistributionBatch(buildBatchBody());
    const created = res?.data;
    const id = created?._id;
    if (!id) {
      throw new Error('Failed to create batch');
    }
    setBatchId(id);
    setBatchStatus('locked');
    setBatchVaultAddress(parseVaultAddress(created?.vaultAddress) || daoVault);
    return id;
  };

  const buildOnChainPreview = async () => {
    if (!chainCtx || !selectedItems.length) {
      throw new Error('Select at least one revenue item');
    }
    const { vault, payoutExec, daoTreasury, platformTreasury, decimals } = chainCtx;
    const revenueIds = [];
    let totalWei = ethers.BigNumber.from(0);

    for (const item of selectedItems) {
      if (!item.onchainRequestId) {
        throw new Error(`Revenue item missing onchainRequestId`);
      }
      const id = ethers.BigNumber.from(item.onchainRequestId);
      const rev = await vault.getRevenue(id);
      if (rev.revenueShared) {
        throw new Error(`Revenue ${item.onchainRequestId} was already distributed on-chain`);
      }
      revenueIds.push(id);
      totalWei = totalWei.add(rev.paymentAmount);
    }

    const [recipients, amounts] = await payoutExec.calculatePayout(
      totalWei,
      Number(splitVersion),
      lpHolders,
      daoTreasury,
      platformTreasury,
    );

    // Keep recipient type by payout slot index (same as contract output order),
    // so duplicated addresses across groups still show correct type per row.
    const lpCount = lpHolders.length;
    const daoIndex = recipients.length - 2;
    const platformIndex = recipients.length - 1;
    const distributions = recipients.map((addr, i) => {
      let recipientType = 'lp_holder';
      if (i === daoIndex) recipientType = 'treasury';
      else if (i === platformIndex) recipientType = 'platform';
      else if (i >= lpCount) recipientType = 'lp_holder';
      return {
        recipientAddress: addr,
        recipientType,
        amount: ethers.utils.formatUnits(amounts[i], decimals),
      };
    });

    const gross = ethers.utils.formatUnits(totalWei, decimals);
    const lpTotal = distributions
      .filter((d) => d.recipientType === 'lp_holder')
      .reduce((s, d) => s + Number(d.amount), 0);
    const treasuryTotal = distributions
      .filter((d) => d.recipientType === 'treasury')
      .reduce((s, d) => s + Number(d.amount), 0);
    const platformTotal = distributions
      .filter((d) => d.recipientType === 'platform')
      .reduce((s, d) => s + Number(d.amount), 0);

    return {
      gross,
      lpTotal: String(lpTotal),
      treasuryTotal: String(treasuryTotal),
      platformTotal: String(platformTotal),
      distributions,
      revenueIds,
    };
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const p = await buildOnChainPreview();
      setPreview(p);
    } catch (err) {
      revenueSharingMessage.error(formatRevenueSharingErrorMessage(err, 'Preview failed'));
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (viewOnly) return;
    if (open && activeStep === 4 && selectedItems.length && lpHolders.length && splitVersion && chainCtx) {
      runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeStep, selectedIds, lpHolders, splitVersion, chainCtx, viewOnly]);

  const runOnChainDistribute = async (previewData) => {
    if (!chainCtx || !isDaoOperator) {
      throw new Error('Connect a wallet with DAO_OPERATOR_ROLE on the Vault');
    }
    await ensureCorrectChain(currentDao?.chainId);
    const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
    const signer = provider.getSigner();
    const vault = new ethers.Contract(effectiveVault, PaymentVaultABI, signer);
    const tx = await vault.distributeRevenues(
      previewData.revenueIds,
      Number(splitVersion),
      lpHolders,
    );
    const receipt = await tx.wait();
    return receipt?.transactionHash || tx.hash;
  };

  const handleSaveLater = async () => {
    setSubmitAction('saveLater');
    setSubmitting(true);
    try {
      if (activeStep === 4 && !preview) {
        await runPreview();
      }
      await createBatchIfNeeded();
      if (onSuccess) onSuccess({ action: 'saved' });
      onClose();
    } catch (err) {
      const msg = formatRevenueSharingErrorMessage(err, 'Save failed');
      if (onSuccess) {
        onSuccess({
          action: 'save_failed',
          message: isTxUserRejection(err) ? 'Transaction was rejected.' : msg,
          severity: isTxUserRejection(err) ? 'warning' : 'error',
        });
      }
    } finally {
      setSubmitting(false);
      setSubmitAction(null);
    }
  };

  const handleDistributeNow = async () => {
    setSubmitAction('distribute');
    setSubmitting(true);
    try {
      let previewData = preview;
      if (!previewData) {
        previewData = await buildOnChainPreview();
        setPreview(previewData);
      }
      const id = await createBatchIfNeeded();
      const txHash = await runOnChainDistribute(previewData);
      await executeDistributionBatch(id, {
        txHash,
        distributions: previewData.distributions,
      });
      if (onSuccess) onSuccess({ action: 'distributed' });
      onClose();
    } catch (err) {
      const msg = formatRevenueSharingErrorMessage(err, 'Distribution failed');
      if (onSuccess) {
        onSuccess({
          action: 'distribute_failed',
          message: isTxUserRejection(err) ? 'Transaction was rejected.' : msg,
          severity: isTxUserRejection(err) ? 'warning' : 'error',
        });
      }
    } finally {
      setSubmitting(false);
      setSubmitAction(null);
    }
  };

  const toggleItem = (id) => {
    const sid = String(id);
    setSelectedIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  };

  const handleAddLp = () => {
    const a = newLpAddress.trim();
    if (!isValidEthAddress(a)) {
      revenueSharingMessage.error('Invalid LP address');
      return;
    }
    setLpHolders((prev) => [...new Set([...prev, a.toLowerCase()])]);
    setNewLpAddress('');
  };

  const handleLpFileChange = async (event) => {
    const inputEl = event.currentTarget || lpFileInputRef.current;
    const file = inputEl?.files?.[0];
    if (!file) return;

    setLpFileName(file.name);

    try {
      const text = await file.text();
      const { addresses, error: parseError } = parseLpHolderCsvText(text);
      if (parseError) {
        revenueSharingMessage.error(parseError);
        setLpFileName('');
        return;
      }
      setLpHolders(addresses);
    } catch (err) {
      revenueSharingMessage.error(err?.message || 'Failed to read CSV file');
      setLpFileName('');
    } finally {
      if (inputEl) {
        inputEl.value = '';
      }
    }
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(itemsInCycle.map((i) => String(i._id)));
    } else {
      setSelectedIds([]);
    }
  };

  const canNext = () => {
    if (activeStep === 0) return periodStart && periodEnd;
    if (activeStep === 1) return !revenueItemsLoading && selectedIds.length > 0;
    if (activeStep === 2) return splitVersion !== '';
    if (activeStep === 3) return lpHolders.length > 0;
    return true;
  };

  const renderStepContent = () => {
    const step4Initializing =
      activeStep === 4 &&
      (((executeOnly || viewOnly) && loading) || (!viewOnly && !walletRoleChecked));

    if (loading && activeStep !== 4) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress sx={{ color: colors.accent }} />
        </Box>
      );
    }

    if (step4Initializing) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress sx={{ color: colors.accent }} />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: colors.textSecondary, fontFamily: 'Questrial, sans-serif', fontSize: '0.95rem' }}>
              Choose the date range for this revenue sharing cycle. In the next step you will select confirmed revenue
              items from this period.
            </Typography>
            <Box sx={formGridSx}>
              <Box sx={fieldBlockStyles}>
                <Typography sx={fieldLabelStyles}>
                  Cycle start{' '}
                  <Box component="span" sx={requiredSpanSx}>
                    *
                  </Box>
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  sx={dateFormFieldStyles}
                  inputProps={periodEnd ? { max: periodEnd } : undefined}
                />
              </Box>
              <Box sx={fieldBlockStyles}>
                <Typography sx={fieldLabelStyles}>
                  Cycle end{' '}
                  <Box component="span" sx={requiredSpanSx}>
                    *
                  </Box>
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  sx={dateFormFieldStyles}
                  inputProps={periodStart ? { min: periodStart } : undefined}
                />
              </Box>
            </Box>
          </Box>
        );
      case 1:
        return (
          <TableContainer
            sx={{
              ...tableContainerStyles,
              maxHeight: 360,
              '& .MuiTable-root': { minWidth: 640 },
              '& .MuiTableCell-root': { py: 1.25, px: 1.5 },
              '& .MuiTableCell-paddingCheckbox': {
                verticalAlign: 'middle',
                width: 48,
                px: 1,
              },
              '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root': {
                verticalAlign: 'middle',
              },
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allInCycleSelected}
                      indeterminate={someInCycleSelected}
                      onChange={(e) => handleToggleSelectAll(e.target.checked)}
                      sx={{
                        p: 0.5,
                        color: colors.accent,
                        '&.Mui-checked': { color: colors.accent },
                        '&.MuiCheckbox-indeterminate': { color: colors.accent },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Revenue type</TableCell>
                  <TableCell sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Amount</TableCell>
                  <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>Payment date</TableCell>
                  <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap' }}>Payer address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenueItemsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={20} sx={{ color: colors.accent }} />
                        <Typography sx={{ color: colors.textSecondary }}>Loading revenue items...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {itemsInCycle.map((item) => (
                      <TableRow key={item._id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedIds.includes(String(item._id))}
                            onChange={() => toggleItem(item._id)}
                            sx={{
                              p: 0.5,
                              color: colors.accent,
                              '&.Mui-checked': { color: colors.accent },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={primaryValueStyles}>{formatRevenueType(item.revenueType)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={primaryValueStyles}>
                            {item.amount} {item.currency || 'USDT'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ ...primaryValueStyles, whiteSpace: 'nowrap' }}>
                            {formatDateTime(item.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            title={item.fromAddress}
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              color: colors.textPrimary,
                            }}
                          >
                            {item.fromAddress ? shortenAddress(item.fromAddress) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!itemsInCycle.length && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align="center"
                          sx={{ py: 5, color: colors.textPrimary, fontFamily: 'Questrial, sans-serif' }}
                        >
                          {eligibleItems.length
                            ? 'No confirmed revenue items in this cycle period'
                            : 'No eligible revenue items'}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case 2:
        return (
          <Box sx={fieldBlockStyles}>
            <Typography sx={fieldLabelStyles}>Split config version</Typography>
            <Select
              fullWidth
              value={splitVersion}
              onChange={(e) => setSplitVersion(e.target.value)}
              displayEmpty
              renderValue={(val) => {
                if (!val) {
                  return <span style={{ opacity: 0.5 }}>Select split config version</span>;
                }
                const row = splitRows.find((r) => String(r.version) === String(val));
                if (!row) return `Version ${val}`;
                return `v${row.version} — LP ${row.lp}% / DAO ${row.dao}% / Platform ${row.platform}%`;
              }}
              sx={makeSelectSx(false, !!splitVersion)}
              MenuProps={selectMenuProps}
            >
              {splitRows.map((r) => (
                <MenuItem key={r.version} value={String(r.version)}>
                  v{r.version} — LP {r.lp}% / DAO {r.dao}% / Platform {r.platform}%
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={fieldBlockStyles}>
              <Typography sx={fieldLabelStyles}>LP holder addresses (CSV)</Typography>
              <input
                ref={lpFileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleLpFileChange}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={() => lpFileInputRef.current?.click()}
                  sx={{
                    textTransform: 'none',
                    borderColor: colors.accent,
                    color: colors.accent,
                    fontFamily: 'Questrial, sans-serif',
                    '&:hover': { borderColor: colors.accent, backgroundColor: 'rgba(26, 238, 194, 0.08)' },
                  }}
                >
                  Upload CSV
                </Button>
                <Typography
                  sx={{
                    color: lpFileName ? colors.textPrimary : colors.textSecondary,
                    fontFamily: 'Questrial, sans-serif',
                    fontSize: '0.95rem',
                  }}
                >
                  {lpFileName || 'No file chosen'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="0x..."
                value={newLpAddress}
                onChange={(e) => setNewLpAddress(e.target.value)}
                sx={{ ...formFieldStyles, flex: 1 }}
              />
              <Button variant="contained" onClick={handleAddLp} sx={{ ...buttonStyles, textTransform: 'none' }}>
                Add
              </Button>
            </Box>
            <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', fontFamily: 'Questrial, sans-serif' }}>
              {lpHolders.length
                ? `${lpHolders.length} address${lpHolders.length === 1 ? '' : 'es'}`
                : 'No addresses yet — upload a CSV or add manually below.'}
            </Typography>
            <Box sx={modalInnerTableCardSx}>
              <TableContainer sx={{ ...modalInnerTableContainerSx, maxHeight: 220 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }}>#</TableCell>
                      <TableCell>Wallet address</TableCell>
                      <TableCell align="right" sx={{ width: 88 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lpHolders.map((addr, index) => (
                      <TableRow key={addr} hover>
                        <TableCell sx={{ color: colors.textSecondary }}>{index + 1}</TableCell>
                        <TableCell>
                          <Typography
                            title={addr}
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              color: colors.textPrimary,
                              wordBreak: 'break-all',
                            }}
                          >
                            {addr}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => setLpHolders((p) => p.filter((a) => a !== addr))}
                            sx={{
                              minWidth: 0,
                              px: 1.5,
                              py: 0.25,
                              textTransform: 'none',
                              color: '#ff6b6b',
                              fontFamily: 'Questrial, sans-serif',
                              fontSize: '0.85rem',
                              '&:hover': { backgroundColor: 'rgba(255, 59, 48, 0.1)' },
                            }}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!lpHolders.length && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: colors.textSecondary }}>
                          No LP holder addresses
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        );
      case 4:
        return (
          <Box>
            {!viewOnly && walletRoleChecked && !isDaoOperator && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your wallet does not have DAO_OPERATOR_ROLE on this Vault. Connect an authorized wallet to distribute.
              </Alert>
            )}
            {previewLoading && !preview && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={28} sx={{ color: colors.accent }} />
              </Box>
            )}
            {preview && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={previewSummaryGridSx}>
                  {[
                    { label: 'Total revenue', value: preview.gross, accent: true },
                    { label: 'LP holders', value: preview.lpTotal },
                    { label: 'DAO treasury', value: preview.treasuryTotal },
                    { label: 'Platform', value: preview.platformTotal },
                  ].map((item) => (
                    <Box key={item.label} sx={previewSummaryCardSx}>
                      <Typography sx={previewSummaryLabelSx}>{item.label}</Typography>
                      <Typography
                        sx={{
                          ...previewSummaryValueSx,
                          color: item.accent ? colors.accent : colors.textPrimary,
                        }}
                      >
                        {formatUsdt(item.value)} USDT
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Typography sx={{ ...fieldLabelStyles, mb: 0 }}>Payout breakdown</Typography>
                <Box sx={modalInnerTableCardSx}>
                  <TableContainer sx={{ ...modalInnerTableContainerSx, maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: 120 }}>Recipient type</TableCell>
                          <TableCell sx={{ minWidth: 140 }}>Wallet</TableCell>
                          <TableCell align="right" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>
                            Amount (USDT)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedPayoutDistributions.map((d) => (
                          <TableRow key={`${d.recipientAddress}-${d.recipientType}`} hover>
                            <TableCell>
                              <Typography sx={primaryValueStyles}>
                                {formatRecipientTypeLabel(d.recipientType)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                title={d.recipientAddress}
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  wordBreak: 'break-all',
                                  color: colors.textPrimary,
                                }}
                              >
                                {d.recipientAddress || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ ...primaryValueStyles, whiteSpace: 'nowrap' }}>
                                {formatUsdt(d.amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const handleClose = () => {
    if (!submitting && !previewLoading) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: modalPaperStyles }}
    >
      <DialogTitle sx={dialogTitleSx}>
        {viewOnly
          ? 'Revenue sharing cycle'
          : executeOnly
            ? 'Distribute revenues'
            : 'New revenue sharing cycle'}
      </DialogTitle>
      <DialogContent>
        {!executeOnly && !viewOnly && (
          <Stepper activeStep={activeStep} sx={{ mb: 3, '& .MuiStepLabel-label': { color: colors.textSecondary } }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        {renderStepContent()}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={handleClose} disabled={submitting || previewLoading} sx={cancelBtnSx}>
          Cancel
        </Button>
        {!executeOnly && !viewOnly && activeStep > 0 && activeStep < 4 && (
          <Button
            onClick={() => setActiveStep((s) => s - 1)}
            disabled={submitting || previewLoading}
            sx={cancelBtnSx}
          >
            Back
          </Button>
        )}
        {!executeOnly && !viewOnly && activeStep < 4 && (
          <Button
            variant="contained"
            disabled={!canNext() || submitting || previewLoading}
            onClick={() => setActiveStep((s) => s + 1)}
            sx={[buttonStyles, confirmBtnSx]}
          >
            Next
          </Button>
        )}
        {(activeStep === 4 || executeOnly) && !isReadOnly && (
          <>
            <Button
              disabled={
                previewLoading ||
                loading ||
                submitting ||
                !selectedIds.length ||
                Boolean(effectiveBatchId)
              }
              onClick={handleSaveLater}
              sx={cancelBtnSx}
            >
              {submitting && submitAction === 'saveLater' ? (
                <CircularProgress size={22} sx={{ color: colors.accent }} />
              ) : (
                'Save for later'
              )}
            </Button>
            <Button
              variant="contained"
              disabled={
                previewLoading ||
                loading ||
                submitting ||
                !walletRoleChecked ||
                !isDaoOperator ||
                !preview
              }
              onClick={handleDistributeNow}
              sx={[buttonStyles, confirmBtnSx]}
            >
              {submitting && submitAction === 'distribute' ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                'Distribute on-chain'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateBatchModal;
